import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import DataTable, { StatusPill, fmtCurrency, fmtDate } from "../components/DataTable";
import { apiCall } from "../auth/AuthContext";

const STATUS_TONE = (v) => {
  const s = (v || "").toLowerCase();
  if (s.includes("approved") || s === "funded") return "success";
  if (s.includes("pending") || s.includes("review")) return "warning";
  if (s.includes("denied") || s.includes("declin")) return "danger";
  if (s.includes("applied") || s.includes("submitted")) return "info";
  return "neutral";
};

export default function AdminFunding() {
  const [tab, setTab] = useState("personal");
  const [personal, setPersonal] = useState([]);
  const [business, setBusiness] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([apiCall("/api/admin/funding/personal"), apiCall("/api/admin/funding/business")]);
        setPersonal(p.records || []); setBusiness(b.records || []);
      } catch (e) { console.error("AdminFunding load failed", e); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = tab === "personal" ? personal : business;

  const totals = useMemo(() => {
    const all = [...personal, ...business];
    return {
      applied: all.reduce((s, r) => s + (r["Amount Applied"] || 0), 0),
      approved: all.reduce((s, r) => s + (r["Amount Approved"] || 0), 0),
      pending: all.filter((r) => /pending|review|applied|submitted/i.test(r.Status || "")).reduce((s, r) => s + (r["Amount Applied"] || 0), 0),
    };
  }, [personal, business]);

  const baseCols = [
    { key: "Name", label: "Client", sortable: true },
    { key: "Lender Name", label: "Lender" },
    { key: tab === "personal" ? "Product Type" : "Funding Type", label: "Type" },
    { key: "Amount Applied", label: "Applied", fmt: fmtCurrency, sortable: true },
    { key: "Amount Approved", label: "Approved", fmt: fmtCurrency },
    { key: "Status", label: "Status", fmt: (v) => <StatusPill value={v} tone={STATUS_TONE(v)} /> },
    { key: "Application Date", label: "Date", fmt: fmtDate, sortable: true },
  ];

  return (
    <AdminLayout title="FUNDING PIPELINE" kicker="Personal + Business">
      <div className="grid grid-cols-3 gap-px bg-jc-gold/15 mb-8">
        {[
          ["Total Applied", totals.applied],
          ["Total Approved", totals.approved],
          ["Total Pending", totals.pending],
        ].map(([k, v]) => (
          <div key={k} data-testid={`funding-total-${k.toLowerCase().replace(/\s+/g, "-")}`} className="bg-jc-black p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-gold">{k}</div>
            <div className="mt-3 font-display text-3xl text-jc-white">{fmtCurrency(v)}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-5">
        {[
          ["personal", "Personal Credit Records"],
          ["business", "Capital Access Records"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`funding-tab-${k}`}
            className={`px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border transition-colors ${
              tab === k ? "bg-jc-gold text-jc-black border-jc-gold" : "border-jc-gold/30 text-jc-muted hover:text-jc-white hover:border-jc-gold/70"
            }`}
          >{l}</button>
        ))}
      </div>

      <DataTable
        testId="admin-funding-table"
        loading={loading}
        records={rows}
        columns={baseCols}
        searchKeys={["Name", "Lender Name", "Status"]}
        emptyText="No funding records yet."
      />
    </AdminLayout>
  );
}
